/** Freeze message wrapping during resize; commit only after 250ms of unchanged width. */
export function createStableChatWidth(viewport, canvas, options = {}) {
  const view = viewport.ownerDocument.defaultView;
  const delay = options.delay || ((fn, ms) => view.setTimeout(fn, ms));
  const cancel = options.cancel || (id => view.clearTimeout(id));
  const Observer = options.Observer || view.ResizeObserver;
  const previousGutter = viewport.style?.scrollbarGutter;
  // A newly visible vertical scrollbar must not trigger a second text reflow.
  if (viewport.style) viewport.style.scrollbarGutter = 'stable';
  const previousWidth = canvas.style.width;
  const previousSizing = canvas.style.boxSizing;
  let width = 0, target = 0, timer = null, animation = null, disposed = false;
  const bubbleMotions = [];
  const stopMotion = () => { animation?.cancel(); bubbleMotions.splice(0).forEach(item => item.cancel()); };
  const clear = () => { if (timer !== null) cancel(timer); timer = null; };
  const measure = () => {
    const css = view.getComputedStyle(viewport);
    return Math.max(0, viewport.clientWidth - parseFloat(css.paddingLeft || 0) - parseFloat(css.paddingRight || 0));
  };
  const commit = () => {
    timer = null;
    if (disposed || viewport.clientHeight === 0) return;
    const top = viewport.scrollTop;
    const bottom = viewport.scrollHeight - viewport.clientHeight - top <= 24;
    const bounds = viewport.getBoundingClientRect();
    const anchor = bottom ? null : Array.from(canvas.querySelectorAll('[data-message-key], [data-chat-message]'))
      .find(row => row.getBoundingClientRect().bottom > bounds.top + 2);
    const anchorTop = anchor?.getBoundingClientRect().top;
    const visibleBubbles = [];
    for (const node of canvas.querySelectorAll('.message-bubble, .msg-bubble, article.message > .body')) {
      const rect = node.getBoundingClientRect();
      if (rect.bottom >= bounds.top && rect.top <= bounds.top + viewport.clientHeight) visibleBubbles.push({ node, rect });
      if (visibleBubbles.length >= 24) break;
    }
    const oldWidth = width;
    width = target;
    canvas.style.width = `${width}px`;
    // Keep the reader's visible message, rather than jumping to the end of history.
    if (bottom) viewport.scrollTop = viewport.scrollHeight - viewport.clientHeight;
    else viewport.scrollTop = anchor ? top + anchor.getBoundingClientRect().top - anchorTop : top;
    delete viewport.dataset.chatWidthPending;
    viewport.dataset.chatWidthApplied = String(width);
    stopMotion();
    if (!view.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      // Width is written once. Animate only the resulting paint, never text wrapping.
      animation = canvas.animate?.([
        { opacity: .82, transform: `translateX(${width > oldWidth ? 3 : -3}px)` },
        { opacity: 1, transform: 'translateX(0)' },
      ], { duration: 180, easing: 'cubic-bezier(.2,.8,.2,1)' });
      animation?.finished?.catch(() => {});
      // FLIP only visible bubble positions; text takes its final width in one layout.
      for (const { node, rect } of visibleBubbles) {
        const next = node.getBoundingClientRect();
        const dx = rect.left - next.left, dy = rect.top - next.top;
        if (Math.abs(dx) < .5 && Math.abs(dy) < .5) continue;
        const motion = node.animate?.([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }],
          { duration: 180, easing: 'cubic-bezier(.2,.8,.2,1)' });
        if (motion) { bubbleMotions.push(motion); motion.finished?.catch(() => {}); }
      }
    }
  };
  const refresh = () => {
    if (disposed) return;
    const next = Math.round(measure() * 100) / 100;
    if (next <= 0 || viewport.clientHeight === 0) { clear(); delete viewport.dataset.chatWidthPending; return; }
    if (!width) {
      width = target = next;
      canvas.style.boxSizing = 'border-box'; canvas.style.width = `${width}px`;
      viewport.dataset.chatWidthApplied = String(width);
      return;
    }
    if (Math.abs(next - width) < .5) {
      target = width; clear(); delete viewport.dataset.chatWidthPending; return;
    }
    if (timer !== null && Math.abs(next - target) < .5) return;
    clear(); stopMotion(); target = next;
    viewport.dataset.chatWidthPending = 'true';
    timer = delay(commit, 250);
  };
  const observer = Observer ? new Observer(refresh) : null;
  observer?.observe(viewport);
  if (!observer) view.addEventListener('resize', refresh);
  refresh();
  return { refresh, dispose() {
    disposed = true; clear(); stopMotion(); observer?.disconnect();
    if (!observer) view.removeEventListener('resize', refresh);
    canvas.style.width = previousWidth; canvas.style.boxSizing = previousSizing;
    if (viewport.style) viewport.style.scrollbarGutter = previousGutter || '';
    delete viewport.dataset.chatWidthPending; delete viewport.dataset.chatWidthApplied;
  } };
}

const controllers = new WeakMap();
export default {
  inserted(el) {
    const canvas = el.querySelector('[data-chat-width-canvas]');
    if (canvas) controllers.set(el, createStableChatWidth(el, canvas));
  },
  componentUpdated(el) { controllers.get(el)?.refresh(); },
  unbind(el) { controllers.get(el)?.dispose(); controllers.delete(el); },
};
